import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppDispatch, useAppDispatch, useAppSelector } from '../../../../store/store';
import { Cycle } from '../../../../store/models/cycles';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import ROUTE from '../../../../constants/route-constants';
import { useAbilityContext } from '../../../../components/Can/Can';
import { Action } from '../../../../enums/action';
import { EXTENDED_INTERVAL_UPDATE_DATA, PAGE_TITLE } from '../../../../constants/common';
import { IActionsDropdown, IActionsTag } from '../../../../types/tableDropdown';
import IconEditOutlined from '../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import { ElementSize } from '../../../../types/element-size';
import IconBin from '../../../../components/Icons/IconBin/IconBin';
import IconCopy from '../../../../components/Icons/IconCopy/IconCopy';
import IconCheck from '../../../../components/Icons/IconCheck/IconCheck';
import CycleStatus from '../../../../enums/cycle-status.enum';
import IconInputOne from '../../../../components/Icons/IconInputOne/IconInputOne';
import { Screen } from '../../../../store/models/screens';
import IconScreen from '../../../../components/Icons/IconScreen/IconScreen';
import IconSwitcher from '../../../../components/Icons/IconSwitcher/IconSwitcher';
import { Paperclip } from '../../../../components/Icons/Paperclip/Paperclip';
import { OpenReport } from '../../../../components/Icons/OpenReport/OpenReport';
import { filterCyclesBySearchQuery } from '../../utils/filterCyclesBySearchQuery';
import { usePolling } from '../../../../hooks/usePolling';
import { useTranslation } from 'react-i18next';
import { joinActionGroups } from '../../../../utils/actionsDropdown';

export interface ICyclesWithoutScreenInteractor {
  cycles: Cycle[];

  isLoading: boolean;

  contractId: string;
  clientId: string;

  cycleActions: IActionsDropdown[];

  screensBySearch: Screen[];
  getScreensByContractId: AppDispatch['screens']['getAllByContractId'];

  cycleIdAttachScreen: string;
  openSelectedCycleAttachScreen: (cycleId: string) => void;
  closeSelectedCycleAttachScreen: () => void;
  onAttachScreenToCycle: (screenId: string) => Promise<void>;

  cycleIdSettings: string;
  cycleNameSettings: string;
  openSelectedCycleSettings: (cycle: Cycle) => void;
  closeSelectedCycleSettings: () => void;

  cycleIdDelete: string;
  openSelectedCycleDelete: (cycleId: string) => void;
  closeSelectedCycleDelete: () => void;
  onAcceptSelectedCycleDelete: () => void;

  cycleIdComplete: string;
  openSelectedCycleComplete: (cycleId: string) => void;
  closeSelectedCycleComplete: () => void;
  onAcceptSelectedCycleComplete: () => void;

  cycleIdMakeInactive: string;
  openSelectedCycleMakeInactive: (cycleId: string) => void;
  closeSelectedCycleMakeInactive: () => void;
  onAcceptSelectedCycleMakeInactive: () => void;

  cycleIdAttach: string;
  isSaveAttachment: boolean;
  closeSelectedCycleAttach: () => void;
  onAcceptSelectedCycleAttach: () => void;
  onSelectFile: (file: File | null) => void;

  cycleIdDuplicate: string;
  cycleNameDuplicate: string;
  closeSelectedCycleDuplicate: () => void;
  onCycleDuplicated: () => void;
}

export const useCyclesWithoutScreenInteractor = (): ICyclesWithoutScreenInteractor => {
  const [isLoading, setIsLoading] = useState(false);
  const [cycleIdSettings, setCycleIdSettings] = useState<string>('');
  const [cycleNameSettings, setCycleNameSettings] = useState<string>('');
  const [cycleIdDelete, setCycleIdDelete] = useState<string>('');
  const [cycleIdComplete, setCycleIdComplete] = useState<string>('');
  const [cycleIdMakeInactive, setCycleIdMakeInactive] = useState<string>('');
  const [cycleIdAttachScreen, setCycleIdAttachScreen] = useState<string>('');
  const [cycleIdAttach, setCycleIdAttach] = useState<string>('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isSaveAttachment, setIsSaveAttachment] = useState<boolean>(false);
  const [cycleIdDuplicate, setCycleIdDuplicate] = useState<string>('');
  const [cycleNameDuplicate, setCycleNameDuplicate] = useState<string>('');

  const { clientId, contractId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    cycles: {
      updateCycle,
      getScreenCycles,
      getScreenlessCycles,
      getCyclesCount,
      deleteCycle,
      getAttachments,
      resetAttachments,
      saveAttachment,
    },
    screens: { getAllByContractId: getScreensByContractId },
  } = useAppDispatch();

  const { screens: screensBySearch } = useAppSelector((state) => state.screens);
  const { activeScreenlessCycles, searchCycleQuery } = useAppSelector((state) => state.cycles);

  const cycles = useMemo(() => {
    if (!searchCycleQuery) {
      return activeScreenlessCycles;
    }

    return filterCyclesBySearchQuery(activeScreenlessCycles, searchCycleQuery);
  }, [activeScreenlessCycles, searchCycleQuery]);

  const ability = useAbilityContext();

  const [canDeleteCycle, canUpdateCycle, canCompleteCycle, canUpdateCycleReport] = useMemo(() => {
    return [
      ability.can(Action.Delete, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Update, PAGE_TITLE.CYCLE_REPORT),
    ];
  }, [ability]);

  const isChangeLoading = useRef(true);

  const fetchDataHandler = async (contractId: string) => {
    try {
      if (!contractId) {
        return;
      }

      if (isChangeLoading.current) {
        setIsLoading(true);
      }

      await getScreenlessCycles(contractId);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      isChangeLoading.current = false;
    }
  };

  //attach screen to cycle
  const openSelectedCycleAttachScreen = useCallback((cycleId: string) => {
    setCycleIdAttachScreen(cycleId);
  }, []);

  const closeSelectedCycleAttachScreen = useCallback(() => {
    setCycleIdAttachScreen('');
  }, []);

  const onAttachScreenToCycle = useCallback(
    async (screenId: string) => {
      await updateCycle({ id: cycleIdAttachScreen, screenId });
      await fetchDataHandler(contractId);
      await getScreenCycles(screenId);
    },
    [cycleIdAttachScreen, contractId],
  );

  //cycle settings
  const openSelectedCycleSettings = useCallback((cycle: Cycle) => {
    setCycleIdSettings(cycle.id);
    setCycleNameSettings(cycle.name);
  }, []);

  const closeSelectedCycleSettings = useCallback(() => {
    setCycleIdSettings('');
    setCycleNameSettings('');
  }, []);

  //cycle delete
  const openSelectedCycleDelete = useCallback((cycleId: string) => {
    setCycleIdDelete(cycleId);
  }, []);

  const closeSelectedCycleDelete = useCallback(() => {
    setCycleIdDelete('');
  }, []);

  const dataRefetch = async (contractId: string) => {
    await Promise.all([getCyclesCount(contractId), fetchDataHandler(contractId)]);
  };

  const onAcceptSelectedCycleDelete = useCallback(async () => {
    closeSelectedCycleDelete();
    await deleteCycle(cycleIdDelete);

    await dataRefetch(contractId);
  }, [contractId, cycleIdDelete, closeSelectedCycleDelete]);

  //cycle complete
  const openSelectedCycleComplete = useCallback((cycleId: string) => {
    setCycleIdComplete(cycleId);
  }, []);

  const closeSelectedCycleComplete = useCallback(() => {
    setCycleIdComplete('');
  }, []);

  const onAcceptSelectedCycleComplete = useCallback(async () => {
    closeSelectedCycleComplete();
    await updateCycle({ id: cycleIdComplete, status: CycleStatus.Completed });

    await dataRefetch(contractId);
  }, [contractId, cycleIdComplete, closeSelectedCycleComplete]);

  //cycle make inactive
  const openSelectedCycleMakeInactive = useCallback((cycleId: string) => {
    setCycleIdMakeInactive(cycleId);
  }, []);

  const closeSelectedCycleMakeInactive = useCallback(() => {
    setCycleIdMakeInactive('');
  }, []);

  const onAcceptSelectedCycleMakeInactive = useCallback(async () => {
    closeSelectedCycleMakeInactive();
    await updateCycle({ id: cycleIdMakeInactive, status: CycleStatus.InActive });

    await dataRefetch(contractId);
  }, [contractId, cycleIdMakeInactive, closeSelectedCycleMakeInactive]);

  //attach to cycle
  const openSelectedCycleAttach = useCallback((cycleId: string) => {
    setCycleIdAttach(cycleId);
  }, []);

  const closeSelectedCycleAttach = useCallback(() => {
    setCycleIdAttach('');
  }, []);

  const onAcceptSelectedCycleAttach = useCallback(async () => {
    try {
      setIsSaveAttachment(true);
      await saveAttachment({ id: cycleIdAttach, attachment: attachment });
    } finally {
      setIsSaveAttachment(false);
    }
  }, [cycleIdAttach, attachment]);

  const onSelectFile = (file: File | null) => {
    setAttachment(file);
  };

  //multi-duplicate cycle
  const openSelectedCycleDuplicate = useCallback((cycleId: string, cycleName: string) => {
    setCycleIdDuplicate(cycleId);
    setCycleNameDuplicate(cycleName);
  }, []);

  const closeSelectedCycleDuplicate = useCallback(() => {
    setCycleIdDuplicate('');
    setCycleNameDuplicate('');
  }, []);

  const onCycleDuplicated = useCallback(() => {
    dataRefetch(contractId);
  }, [contractId]);

  const editCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />,
      name: t('commonText.edit'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.EDIT_CYCLE_WITHOUT_SCREEN, { clientId, contractId, id: cycleId });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [navigate, clientId, contractId]);

  const deleteCycleFromScreenActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconBin fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.delete'),
      onClick: (cycleId: string) => {
        openSelectedCycleDelete(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleDelete]);

  const completeCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconCheck fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.complete'),
      onClick: (cycleId: string) => {
        openSelectedCycleComplete(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleComplete]);

  const makeInactiveCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => (
        <div className="flex h-4 items-center justify-center text-warning">
          <IconSwitcher size={ElementSize.XXS} />
        </div>
      ),
      name: t('commonText.makeInactive'),
      onClick: (cycleId: string) => {
        openSelectedCycleMakeInactive(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleMakeInactive]);

  const attachToCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <Paperclip stroke="#000" size={ElementSize.XXS} />,
      name: t('commonText.viewAttachments'),
      onClick: (cycleId: string) => {
        openSelectedCycleAttach(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleAttach]);

  const openCycleReportActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <OpenReport stroke="#000" iconClassName="text-inherit" size={ElementSize.XXS} />,
      name: t('commonText.openReport'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.CYCLE_REPORT_WITHOUT_SCREEN, { id: cycleId, clientId, contractId });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [navigate, clientId, contractId]);

  const addScreenToCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconScreen fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.addToScreen'),
      onClick: (cycleId: string) => {
        openSelectedCycleAttachScreen(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleAttachScreen]);

  const openCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconInputOne size={ElementSize.XXS} />,
      name: t('commonText.openChart'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.CYCLE_WITHOUT_SCREEN, {
          clientId,
          contractId,
          id: cycleId,
        });
        window.open(path, '_blank');
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [clientId, contractId]);

  const duplicateCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconCopy fill="#000" size={ElementSize.XXS} />,
      name: 'Multi-duplicate',
      onClick: (cycleId: string, cycleData: Cycle) => {
        openSelectedCycleDuplicate(cycleId, cycleData.name);
      },
      loading: false,
    };
  }, [openSelectedCycleDuplicate]);

  const cycleActions: IActionsDropdown[] = useMemo(() => {
    const viewGroup: IActionsDropdown[] = [
      openCycleActionBtn,
      ...(canUpdateCycle ? [attachToCycleActionBtn] : []),
      ...(canUpdateCycleReport ? [openCycleReportActionBtn] : []),
    ];

    const screenGroup: IActionsDropdown[] = canUpdateCycle ? [addScreenToCycleActionBtn] : [];

    const editGroup: IActionsDropdown[] = canUpdateCycle ? [editCycleActionBtn, duplicateCycleActionBtn] : [];

    const statusGroup: IActionsDropdown[] = [
      ...(canCompleteCycle ? [completeCycleActionBtn] : []),
      ...(canCompleteCycle ? [makeInactiveCycleActionBtn] : []),
    ];

    const deleteGroup: IActionsDropdown[] = canDeleteCycle ? [deleteCycleFromScreenActionBtn] : [];

    return joinActionGroups(viewGroup, screenGroup, editGroup, statusGroup, deleteGroup);
  }, [
    canUpdateCycle,
    canCompleteCycle,
    canDeleteCycle,
    canUpdateCycleReport,
    editCycleActionBtn,
    deleteCycleFromScreenActionBtn,
    addScreenToCycleActionBtn,
    openCycleActionBtn,
    duplicateCycleActionBtn,
    attachToCycleActionBtn,
    openCycleReportActionBtn,
    completeCycleActionBtn,
    makeInactiveCycleActionBtn,
  ]);

  usePolling(() => fetchDataHandler(contractId), EXTENDED_INTERVAL_UPDATE_DATA, [contractId]);

  useEffect(() => {
    if (cycleIdAttach) {
      getAttachments(cycleIdAttach);
    }

    return () => {
      resetAttachments();
    };
  }, [cycleIdAttach]);

  return {
    cycles,

    screensBySearch,
    getScreensByContractId,

    isLoading,
    cycleActions,

    contractId,
    clientId,

    //attach screen to cycle
    cycleIdAttachScreen,
    openSelectedCycleAttachScreen,
    closeSelectedCycleAttachScreen,
    onAttachScreenToCycle,

    //cycle settings
    cycleIdSettings,
    cycleNameSettings,
    openSelectedCycleSettings,
    closeSelectedCycleSettings,

    //delete cycle from screen
    cycleIdDelete,
    openSelectedCycleDelete,
    closeSelectedCycleDelete,
    onAcceptSelectedCycleDelete,

    //cycle complete
    cycleIdComplete,
    openSelectedCycleComplete,
    closeSelectedCycleComplete,
    onAcceptSelectedCycleComplete,

    //cycle make inactive
    cycleIdMakeInactive,
    openSelectedCycleMakeInactive,
    closeSelectedCycleMakeInactive,
    onAcceptSelectedCycleMakeInactive,

    //attach to cycle
    cycleIdAttach,
    isSaveAttachment,
    closeSelectedCycleAttach,
    onAcceptSelectedCycleAttach,
    onSelectFile,

    //multi-duplicate cycle
    cycleIdDuplicate,
    cycleNameDuplicate,
    closeSelectedCycleDuplicate,
    onCycleDuplicated,
  };
};
